import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { MessageCircle, X, Send, Bot as BotIcon, User, LifeBuoy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const GREETING = "Hi! I'm Next Billion Support AI. Ask me anything about bots, staking, shareholder, withdrawals, or KYC. If I can't help, I'll open a support ticket for you.";

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: GREETING }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [escalate, setEscalate] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticket, setTicket] = useState({ subject: "", message: "" });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || busy) return;
    setMsgs((m) => [...m, { role: "user", content: t }]);
    setText("");
    setBusy(true);
    setEscalate(false);
    try {
      const { data } = await api.post("/support/chat", { message: t, session_id: sessionId });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, escalate: data.escalate }]);
      setEscalate(!!data.escalate);
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail);
      setMsgs((m) => [...m, { role: "assistant", content: `⚠ ${detail}. You can also open a support ticket.`, escalate: true }]);
      setEscalate(true);
    } finally {
      setBusy(false);
    }
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    if (!ticket.subject.trim() || !ticket.message.trim()) return;
    try {
      await api.post("/support/tickets", { ...ticket, session_id: sessionId });
      toast.success("Support ticket created. Our team will reply soon.");
      setTicketOpen(false);
      setTicket({ subject: "", message: "" });
      setMsgs((m) => [...m, { role: "assistant", content: "✅ Your support ticket has been created. An admin will reach out shortly." }]);
      setEscalate(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="support-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-40 h-14 w-14 rounded-full bg-nb-cyan text-black shadow-lg shadow-nb-cyan/40 flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Open support chat"
        >
          <MessageCircle size={22}/>
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#8AE234] animate-pulse"/>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div data-testid="support-panel" className="fixed inset-x-2 bottom-2 md:inset-auto md:bottom-6 md:right-6 z-40 w-auto md:w-96 h-[85vh] md:h-[560px] rounded-2xl border border-nb-cyan/30 bg-[#0F1626] shadow-2xl shadow-black/60 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-nb-cyan/10 to-transparent">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-nb-cyan/20 flex items-center justify-center"><BotIcon size={16} className="text-nb-cyan"/></div>
              <div>
                <div className="font-heading text-white text-sm">Next Billion AI Support</div>
                <div className="text-[10px] text-[#8AE234] flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#8AE234] animate-pulse"/>Online</div>
              </div>
            </div>
            <button data-testid="support-close" onClick={()=>setOpen(false)} className="text-slate-400 hover:text-white"><X size={16}/></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="support-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (<div className="h-6 w-6 rounded-full bg-nb-cyan/15 flex items-center justify-center shrink-0 mt-1"><BotIcon size={12} className="text-nb-cyan"/></div>)}
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-nb-cyan text-black" : "bg-[#0A1120] border border-white/5 text-slate-200"}`}>{m.content}</div>
                {m.role === "user" && (<div className="h-6 w-6 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-1"><User size={12} className="text-slate-300"/></div>)}
              </div>
            ))}
            {busy && (<div className="flex gap-2"><div className="h-6 w-6 rounded-full bg-nb-cyan/15 flex items-center justify-center shrink-0 mt-1"><BotIcon size={12} className="text-nb-cyan"/></div><div className="rounded-2xl px-3 py-2 text-sm bg-[#0A1120] border border-white/5 text-slate-400 animate-pulse">Thinking…</div></div>)}
          </div>

          {/* Escalation prompt */}
          {escalate && !ticketOpen && (
            <div className="mx-3 mb-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 flex items-center justify-between gap-2" data-testid="escalate-banner">
              <div className="text-xs text-amber-200 flex items-center gap-1.5"><LifeBuoy size={12}/> Need human help?</div>
              <button data-testid="open-ticket" onClick={()=>{ setTicketOpen(true); setTicket({subject:"", message: msgs.filter(m=>m.role==="user").slice(-1)[0]?.content || ""}); }} className="text-xs px-3 py-1 rounded-full bg-amber-400 text-black font-semibold">Create Ticket</button>
            </div>
          )}

          {/* Ticket form */}
          {ticketOpen ? (
            <form onSubmit={submitTicket} className="p-3 border-t border-white/10 space-y-2 bg-[#0A1120]" data-testid="ticket-form">
              <div className="text-xs uppercase tracking-widest text-nb-cyan">Create Support Ticket</div>
              <input data-testid="ticket-subject" placeholder="Subject" value={ticket.subject} onChange={e=>setTicket({...ticket, subject: e.target.value})} className="nb-input text-sm" required/>
              <textarea data-testid="ticket-message" placeholder="Describe your issue…" value={ticket.message} onChange={e=>setTicket({...ticket, message: e.target.value})} className="nb-input text-sm h-20" required/>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={()=>setTicketOpen(false)} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-300">Cancel</button>
                <button type="submit" data-testid="ticket-submit" className="text-xs px-3 py-1.5 rounded-full bg-nb-cyan text-black font-semibold"><CheckCircle2 size={11} className="inline mr-1"/>Submit</button>
              </div>
            </form>
          ) : (
            <form onSubmit={send} className="p-3 border-t border-white/10 flex items-center gap-2">
              <input data-testid="support-input" value={text} onChange={e=>setText(e.target.value)} placeholder="Type your question…" className="nb-input text-sm flex-1" disabled={busy}/>
              <button type="submit" data-testid="support-send" disabled={busy || !text.trim()} className="h-9 w-9 rounded-lg bg-nb-cyan text-black flex items-center justify-center disabled:opacity-40"><Send size={14}/></button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
